import { useEffect, useState, useCallback } from "react";
import {
  Bell,
  BellSimple,
  Warning,
  X,
  ArrowClockwise,
  TrendUp,
  CreditCard,
  Receipt,
  CheckCircle,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { toast } from "sonner";

const KIND_META = {
  subscription_hike: { Icon: TrendUp, label: "Suscripción subió", color: "#F59E0B" },
  unusual_charge: { Icon: MagnifyingGlass, label: "Cargo inusual", color: "#F97316" },
  upcoming_due: { Icon: Receipt, label: "Pago próximo", color: "#2563EB" },
  new_recurring: { Icon: BellSimple, label: "Nuevo recurrente", color: "#8B5CF6" },
  high_apr_debt: { Icon: CreditCard, label: "APR alto", color: "#EF4444" },
  low_cash: { Icon: Warning, label: "Cash bajo", color: "#FACC15" },
};

const SEVERITY_STYLES = {
  info: { ring: "border-blue-500/30", bg: "bg-blue-500/5" },
  warning: { ring: "border-yellow-500/40", bg: "bg-yellow-500/5" },
  critical: { ring: "border-red-500/40", bg: "bg-red-500/8" },
};

/**
 * Notification bell + dropdown. Auto-refreshes alerts every 60s.
 */
export default function AlertsBell() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/alerts?dismissed=false");
      setAlerts(data);
    } catch (err) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/finance/alerts/scan");
      if (data.created > 0) {
        toast.success(`${data.created} alerta(s) nueva(s)`);
      } else {
        toast.info("Sin novedades");
      }
      await load();
    } catch (err) {
      toast.error("No se pudo escanear");
    } finally {
      setScanning(false);
    }
  };

  const dismiss = async (id) => {
    try {
      await api.post(`/finance/alerts/${id}/dismiss`);
      setAlerts((prev) => prev.filter((a) => a.alert_id !== id));
    } catch (err) {
      toast.error("No se pudo descartar");
    }
  };

  const dismissAll = async () => {
    if (alerts.length === 0) return;
    if (!window.confirm(`¿Descartar ${alerts.length} alerta(s)?`)) return;
    try {
      await api.post("/finance/alerts/dismiss-all");
      setAlerts([]);
      toast.success("Alertas descartadas");
    } catch (err) {
      toast.error("Error al descartar");
    }
  };

  const criticalCount = alerts.filter((a) => a.severity === "critical").length;

  return (
    <div className="relative">
      <button
        data-testid="alerts-bell"
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        title="Alertas del CFO"
        className="w-10 h-10 relative flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white transition"
      >
        <Bell size={16} weight={alerts.length > 0 ? "fill" : "bold"} style={alerts.length > 0 ? { color: criticalCount > 0 ? "#EF4444" : "#FACC15" } : {}} />
        {alerts.length > 0 && (
          <span
            data-testid="alerts-count"
            className={`absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-mono font-bold text-white flex items-center justify-center ${
              criticalCount > 0 ? "bg-red-500" : "bg-[#FACC15] !text-black"
            }`}
          >
            {alerts.length}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            data-testid="alerts-dropdown"
            className="absolute top-12 right-0 z-40 w-[380px] max-w-[92vw] rounded-2xl border border-white/12 bg-[#0F1017] shadow-2xl overflow-hidden"
            style={{ animation: "slideDown 180ms cubic-bezier(0.16, 1, 0.3, 1)" }}
          >
            <div className="p-4 border-b border-white/8 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
                  ALERTAS · CFO INTELLIGENCE
                </div>
                <div className="text-base font-heading font-bold text-white mt-0.5">
                  {alerts.length === 0 ? "Todo en orden" : `${alerts.length} alerta(s)`}
                </div>
              </div>
              <button
                data-testid="alerts-scan-btn"
                onClick={scan}
                disabled={scanning}
                className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#FACC15] hover:text-yellow-300"
              >
                {scanning ? (
                  <ArrowClockwise size={11} weight="bold" className="animate-spin" />
                ) : (
                  <MagnifyingGlass size={11} weight="bold" />
                )}
                Escanear
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {alerts.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle size={28} weight="fill" className="mx-auto mb-2 text-emerald-400/70" />
                  <div className="text-sm font-heading font-bold text-white mb-1">
                    Sin alertas activas
                  </div>
                  <div className="text-[11px] font-mono text-white/50">
                    Click &quot;Escanear&quot; para revisar tus finanzas.
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-white/6">
                  {alerts.map((a) => {
                    const meta = KIND_META[a.kind] || KIND_META.unusual_charge;
                    const style = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info;
                    const Icon = meta.Icon;
                    return (
                      <div
                        key={a.alert_id}
                        data-testid={`alert-${a.alert_id}`}
                        className={`p-3 flex items-start gap-3 hover:bg-white/4 group ${style.bg}`}
                      >
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${style.ring}`}
                          style={{ backgroundColor: `${meta.color}18` }}
                        >
                          <Icon size={14} weight="fill" style={{ color: meta.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-heading font-bold text-white truncate">
                            {a.title}
                          </div>
                          <div className="text-[11px] font-mono text-white/60 leading-relaxed mt-0.5">
                            {a.body}
                          </div>
                        </div>
                        <button
                          data-testid={`alert-dismiss-${a.alert_id}`}
                          onClick={() => dismiss(a.alert_id)}
                          className="w-6 h-6 rounded hover:bg-white/10 text-white/40 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition shrink-0"
                        >
                          <X size={11} weight="bold" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {alerts.length > 0 && (
              <div className="p-3 border-t border-white/8 flex justify-end">
                <button
                  data-testid="alerts-dismiss-all"
                  onClick={dismissAll}
                  className="text-[11px] font-mono uppercase tracking-widest text-white/50 hover:text-white"
                >
                  Descartar todas
                </button>
              </div>
            )}
          </div>
        </>
      )}
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-8px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
